/**
 * Sanitize GrapesJS design_json data
 *
 * Fixes common GrapesJS bugs that corrupt data during save cycles:
 * 1. Removes nested "style" properties from style objects
 *    Bug: { style: { style: "...", color: "#fff" } }
 *    Fix: { style: { color: "#fff" } }
 * 2. Removes duplicated "style:" prefixes from attribute strings
 *    Bug: style="style:color:red"
 *    Fix: style="color:red"
 *
 * @param projectData - GrapesJS project data
 * @returns Sanitized project data
 */
export function sanitizeGrapesJSProjectData(projectData: any): any {
  if (!projectData || typeof projectData !== 'object') {
    return projectData;
  }

  // Deep clone to avoid mutating original
  const sanitized = JSON.parse(JSON.stringify(projectData));

  /**
   * Recursively clean style attributes that have been corrupted
   */
  function cleanStyleAttributes(obj: any): any {
    if (!obj || typeof obj !== 'object') {
      return obj;
    }

    // Handle arrays
    if (Array.isArray(obj)) {
      return obj.map(item => cleanStyleAttributes(item));
    }

    // Handle objects
    const cleaned: any = {};
    for (const [key, value] of Object.entries(obj)) {
      // Fix corrupted style strings
      if (key === 'style' && typeof value === 'string') {
        let cleanedStyle = value;

        // Remove all "style:" prefixes (handles "style:style:style:..." pattern)
        while (cleanedStyle.startsWith('style:')) {
          cleanedStyle = cleanedStyle.substring(6);
        }

        cleaned[key] = cleanedStyle;
      }
      // FIX: Handle style objects that have a nested "style" property
      // GrapesJS bug creates: { style: { style: "...", color: "#fff" } }
      else if (key === 'style' && typeof value === 'object' && !Array.isArray(value)) {
        const cleanedStyleObj: any = {};
        for (const [styleKey, styleValue] of Object.entries(value)) {
          if (styleKey === 'style' && typeof styleValue === 'string') {
            // Skip the nested "style" property - it's corrupted data
            continue;
          } else {
            cleanedStyleObj[styleKey] = cleanStyleAttributes(styleValue);
          }
        }
        cleaned[key] = cleanedStyleObj;
      }
      // Recurse into attributes object
      else if (key === 'attributes' && typeof value === 'object') {
        const cleanedAttrs: any = {};
        for (const [attrKey, attrValue] of Object.entries(value)) {
          if (attrKey === 'style' && typeof attrValue === 'string') {
            let cleanedAttrStyle = attrValue;
            while (cleanedAttrStyle.startsWith('style:')) {
              cleanedAttrStyle = cleanedAttrStyle.substring(6);
            }
            cleanedAttrs[attrKey] = cleanedAttrStyle;
          } else {
            cleanedAttrs[attrKey] = cleanStyleAttributes(attrValue);
          }
        }
        cleaned[key] = cleanedAttrs;
      }
      // Recurse into nested objects/arrays
      else if (typeof value === 'object') {
        cleaned[key] = cleanStyleAttributes(value);
      }
      // Keep primitives as-is
      else {
        cleaned[key] = value;
      }
    }

    return cleaned;
  }

  return cleanStyleAttributes(sanitized);
}
